import SimpleDelegator from '../simple_delegator';
import AbortableRequest from './abortable_request';

/**
 * A decorator that delays execution of a request by a specified duration.
 *
 * Internally wraps the request with {@link AbortableRequest} so that calling `cancel()`
 * both clears the pending timer and rejects the promise (preventing zombie promises
 * that never settle).
 *
 * @example
 *   const request = new DelayedRequest(new FetchRequest('GET', '/api/poll'), 500);
 *   const promise = request.perform(); // fetch fires after 500ms
 *   request.cancel(); // clears timer, rejects with AbortError
 */
class DelayedRequest extends SimpleDelegator {
  constructor(request, delay) {
    super(new AbortableRequest(request));
    this.timeoutId = null;
    this.delay = delay;
  }

  perform() {
    return new Promise((resolve, reject) => {
      if (this.signal.aborted) {
        return reject(this.signal.reason);
      }

      this.signal.addEventListener('abort', () => reject(this.signal.reason));

      this.timeoutId = setTimeout(
        () => this.__target__.perform().then(resolve, reject),
        this.delay
      );
    });
  }

  cancel(reason) {
    clearTimeout(this.timeoutId);
    this.__target__.cancel(reason);
  }
}

export default DelayedRequest;
